import { Button, Chip, Dropdown, getCalendarFormat, Icon } from "@cakemail-org/ui-components-v2"
import { Stack, Typography } from "@mui/material"
import { t } from "i18next"
import { getDomain } from "../../../../utils/emailUtils"
import { PUBLIC_DOMAINS } from "../..//contants"
import { EDomainAuthStatus } from "../../types"
import { TSenderRowProps } from "./types"

export function SenderRow({
    sender,
    index,
    loading,
    domainInfo,
    handleViewSender,
    handleDeleteSender,
    handleResendConfirmation,
    handleAuthenticateDomain
}: TSenderRowProps) {
    const domain = getDomain(sender)
    const isDomainNotPublic = !PUBLIC_DOMAINS.includes(domain.toLowerCase())
    const isAuthenticateDisabled = !isDomainNotPublic || domainInfo?.authenticated === EDomainAuthStatus.Authenticated

    const dropdownOptions = [
        ...(!isAuthenticateDisabled ? [{
            isAction: true,
            value: "authenticate-domain",
            text: t("general-authenticate-domain"),
            onClick: () => handleAuthenticateDomain?.(domain)
        }] : []),
        {
            isAction: true,
            value: "delete-sender",
            text: t("general-delete"),
            onClick: () => handleDeleteSender(sender)
        }
    ]

    const actions = (
        <Stack direction="row" gap={2} alignItems="center" justifyContent="flex-end" minWidth="100%">
            {sender.confirmed ? (
                <Chip
                    label={t("general-verified")}
                    color="secondary"
                    variant="outlined"
                    size="small"
                />
            ) : sender.last_confirmation_sent_on ? (
                <>
                    <Typography variant="body2" color="text.secondary">
                        {t("senders-listing-verification-date-message", {
                            date: getCalendarFormat({ unixDate: sender.last_confirmation_sent_on })
                        })}
                    </Typography>
                    <Button
                        onClick={() => handleResendConfirmation(sender)}
                        size="small"
                        color="secondary"
                        variant="text"
                        disabled={loading}
                    >
                        {t("general-resend")}
                    </Button>
                </>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    {t("senders-listing-sending-confirmation-email")}
                </Typography>
            )}

            <Button
                onClick={() => handleViewSender(sender)}
                size="small"
                color="body1"
                variant="outlined"
                disabled={loading}
            >
                {t("general-view_one")}
            </Button>

            {dropdownOptions.length > 0 && (
                <Dropdown
                    options={dropdownOptions}
                    placeholder={""}
                    color="body1"
                    variant="outlined"
                    iconOnly
                />
            )}
        </Stack>
    )

    let domainAuthChip: React.ReactElement

    if (!isDomainNotPublic) {
        domainAuthChip = (
            <Typography variant="body2" color="text.secondary">
                {t("senders-listing-domain-is-public")}
            </Typography>
        )
    } else if (!domainInfo?.authenticated) {
        domainAuthChip = (
            <Typography variant="body2" color="text.secondary">
                {t("senders-listing-domain-not-verified")}
            </Typography>
        )
    } else {
        let iconName: "Domainauthenticated18" | "Domainblocked18" | "Domainunauthenticated18"
        let iconColor: "secondary" | "error" | "warning"
        let textColor: string
        let translationKey: string

        if (domainInfo.authenticated === EDomainAuthStatus.Authenticated) {
            iconName = "Domainauthenticated18"
            iconColor = "secondary"
            textColor = "body2"
            translationKey = "senders-listing-domain-authenticated"
        } else if (domainInfo.authenticated === EDomainAuthStatus.Blocked) {
            iconName = "Domainblocked18"
            iconColor = "error"
            textColor = "error.main"
            translationKey = "senders-listing-domain-locked"
        } else {
            iconName = "Domainunauthenticated18"
            iconColor = "warning"
            textColor = "warning.main"
            translationKey = "senders-listing-domain-unauthenticated"
        }

        domainAuthChip = (
            <Stack direction="row" gap={1} alignItems="center">
                <Icon name={iconName} color={iconColor} />
                <Typography color={textColor}>
                    {t(translationKey)}
                </Typography>
            </Stack>
        )
    }

    return {
        id: index,
        name: { id: sender.name, content: sender.name || "" },
        email: sender.email,
        domain: domain,
        domain_authenticated: domainAuthChip,
        confirmed_on: getCalendarFormat({ unixDate: sender.confirmed_on }) || "",
        actions: actions
    }
}